import type { ApiContext } from "../context";
import { type ApiNotAuthorized, InvalidFormField, ValidationError } from "../errors";
import { parse } from "../model/common";
import { UploadedImage } from "../model/store";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";

/**
 * `/uploads`: hosting for images the merchant supplies, as opposed to the ones
 * the scan pipeline produces.
 *
 * Deliberately not scoped to a store — the onboarding wizard asks for a logo on
 * the screen that creates the shop, so at upload time there is no store to scope
 * it to. Any signed-in merchant may store an image; the URL only means something
 * once it is saved onto a record they own.
 *
 * Images travel as `data:` URLs, like the scan pipeline's, because the whole API
 * is JSON. PNG, JPEG, WEBP, GIF and SVG, up to 2 MB decoded.
 */
export class UploadsApi {
  constructor(private readonly t: Transport) {}

  /** A shop logo. */
  async logo(ctx: ApiContext, image: string): Promise<Result<UploadedImage, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.post(ctx, "/uploads/logo", { body: { image } });
    return forward(
      r,
      (j) => parse(UploadedImage, j),
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        return e;
      },
    );
  }

  /**
   * The flyer for a live stream. Its own prefix rather than the logo one, so a
   * lifecycle rule can treat stream art as disposable and a shop's identity as not.
   */
  async liveCover(ctx: ApiContext, image: string): Promise<Result<UploadedImage, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.post(ctx, "/uploads/live-cover", { body: { image } });
    return forward(
      r,
      (j) => parse(UploadedImage, j),
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        return e;
      },
    );
  }
}
